import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/features/auth/AuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Logo } from '@/components/Logo';
import { getApiErrorMessage } from '@/lib/api-client';

export default function LoginPage() {
  const { loginWithGoogle } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();

  // Se o callback do Google redirecionou de volta com ?error=..., mostra mensagem.
  useEffect(() => {
    const err = params.get('error');
    if (err) {
      const msg = params.get('message') || friendlyError(err);
      setSubmitError(msg);
      const next = new URLSearchParams(params);
      next.delete('error');
      next.delete('message');
      setParams(next, { replace: true });
    }
  }, [params, setParams]);

  const onClick = async () => {
    setSubmitError(null);
    setSubmitting(true);
    try {
      await loginWithGoogle(); // redireciona via window.location
    } catch (err) {
      setSubmitting(false);
      setSubmitError(getApiErrorMessage(err, 'Falha ao iniciar login.'));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-base px-4">
      <Card className="w-full max-w-sm">
        <CardBody>
          <div className="mb-8 flex flex-col items-center text-center">
            <Logo className="h-12 w-auto" />
            <p className="mt-3 text-sm text-text-muted">Hub de conhecimento do time</p>
          </div>

          <Button onClick={onClick} loading={submitting} className="w-full">
            <GoogleIcon /> Entrar com Google
          </Button>

          {submitError && (
            <p className="mt-4 text-sm text-red-400 text-center" role="alert">
              {submitError}
            </p>
          )}

          <p className="mt-6 text-xs text-center text-text-muted">
            Acesso restrito a contas <strong>@starbridge.com.br</strong>.
          </p>
        </CardBody>
      </Card>
    </div>
  );
}

function friendlyError(code: string): string {
  switch (code) {
    case 'forbidden':
      return 'Sua conta Google não é @starbridge.com.br ou está desativada.';
    case 'unauthorized':
      return 'Sessão de autenticação expirou. Tente novamente.';
    default:
      return 'Falha no login. Tente novamente.';
  }
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 48 48" width="18" height="18" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8a12 12 0 1 1 0-24c3 0 5.8 1.1 8 3l5.7-5.7A20 20 0 1 0 24 44a20 20 0 0 0 19.6-23.5z"
      />
      <path
        fill="#FF3D00"
        d="m6.3 14.7 6.6 4.8C14.6 16 19 13 24 13c3 0 5.8 1.1 8 3l5.7-5.7A20 20 0 0 0 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 10-2 13.5-5.2l-6.2-5.2A12 12 0 0 1 12.7 28.5l-6.5 5A20 20 0 0 0 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3a12 12 0 0 1-4 5.6l6.2 5.2C41.4 35.5 44 30.2 44 24c0-1.2-.1-2.4-.4-3.5z"
      />
    </svg>
  );
}
